/**
 * WeddingCardMenu: the kebab (\u22ee) menu on a WeddingCard, exposing
 * Rename, Duplicate, Archive/Unarchive, and Delete. Deliberately a
 * sibling element to the card's own <Link>, not nested inside it — an
 * interactive button inside an <a>-equivalent element is a real HTML/
 * accessibility problem (nested interactive elements, ambiguous click
 * targets), so this renders as an absolutely-positioned overlay instead,
 * with its own stopPropagation so opening the menu never triggers the
 * card's navigation underneath it.
 */

import { useEffect, useRef, useState } from 'react';

export type WeddingCardAction = 'rename' | 'duplicate' | 'archive' | 'unarchive' | 'delete';

interface WeddingCardMenuProps {
  isArchived: boolean;
  /** Delete is admin-only at the RLS level (see
   * weddings_delete_admin_only in migration 0003) — this hides the
   * option entirely for coordinators rather than showing a button that
   * would just fail, mirroring the real database-level boundary instead
   * of guessing at an error message after the fact. */
  canDelete: boolean;
  onAction: (action: WeddingCardAction) => void;
}

export function WeddingCardMenu({ isArchived, canDelete, onAction }: WeddingCardMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  function handleSelect(action: WeddingCardAction) {
    setOpen(false);
    onAction(action);
  }

  return (
    <div ref={menuRef} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label="Wedding options"
        aria-expanded={open}
        className="flex h-7 w-7 items-center justify-center rounded-full text-ink-400 hover:bg-ivory-200 hover:text-ink-600"
      >
        {'\u22EE'}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-8 z-10 w-44 overflow-hidden rounded-lg border border-ivory-300 bg-white shadow-elevated"
        >
          <button
            role="menuitem"
            onClick={(e) => {
              e.preventDefault();
              handleSelect('rename');
            }}
            className="block w-full px-3 py-2 text-left font-body text-sm text-ink-600 hover:bg-ivory-100"
          >
            Rename
          </button>
          <button
            role="menuitem"
            onClick={(e) => {
              e.preventDefault();
              handleSelect('duplicate');
            }}
            className="block w-full px-3 py-2 text-left font-body text-sm text-ink-600 hover:bg-ivory-100"
          >
            Duplicate
          </button>
          <button
            role="menuitem"
            onClick={(e) => {
              e.preventDefault();
              handleSelect(isArchived ? 'unarchive' : 'archive');
            }}
            className="block w-full px-3 py-2 text-left font-body text-sm text-ink-600 hover:bg-ivory-100"
          >
            {isArchived ? 'Unarchive' : 'Archive'}
          </button>
          {canDelete && (
            <>
              <div className="border-t border-ivory-200" />
              <button
                role="menuitem"
                onClick={(e) => {
                  e.preventDefault();
                  handleSelect('delete');
                }}
                className="block w-full px-3 py-2 text-left font-body text-sm text-danger hover:bg-danger/5"
              >
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
